import React from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { NotFoundErrorPageAsync } from '@atlassian/jira-error-pages/src/async.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import type { virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_featuresRef$key } from '@atlassian/jira-relay/src/__generated__/virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_featuresRef.graphql';
import type { virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_projectRef$key } from '@atlassian/jira-relay/src/__generated__/virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_projectRef.graphql';
import { Redirect } from '@atlassian/react-resource-router';

export type AllowMode = 'configured-and-entitled' | 'any-configured' | 'not-configured';

export const VirtualAgentPageWrapper = ({
	children,
	allowMode,
	featuresRef,
	projectRef,
}: React.PropsWithChildren<{
	allowMode: AllowMode;
	projectRef: virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_projectRef$key | null;
	featuresRef: virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_featuresRef$key;
}>) => {
	const projectData = useFragment(
		graphql`
			fragment virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_projectRef on JiraProject {
				key
				virtualAgentConfiguration {
					__typename
					... on VirtualAgentConfiguration {
						__typename
					}
				}
			}
		`,
		projectRef,
	);

	const featuresData = useFragment(
		graphql`
			fragment virtualAgentPageWrapper_servicedeskSettingsVirtualAgentCommon_featuresRef on Query
			@argumentDefinitions(cloudId: { type: "ID!" }, checkForAiEnabledFF: { type: "Boolean!" }) {
				virtualAgent @optIn(to: ["VirtualAgent"]) {
					virtualAgentEntitlements(cloudId: $cloudId) {
						isVirtualAgentAvailable
						isAiEnabledInAdminHub @include(if: $checkForAiEnabledFF)
					}
				}
			}
		`,
		featuresRef,
	);

	if (projectData == null) {
		// If the project data couldn't be loaded, show a 404
		return <NotFoundErrorPageAsync />;
	}

	const { key: projectKey, virtualAgentConfiguration } = projectData;

	const vaFeatures = featuresData.virtualAgent?.virtualAgentEntitlements;
	const siteCanUseVirtualAgent: boolean =
		!!vaFeatures?.isVirtualAgentAvailable &&
		(ff('check-for-ai-enabling-in-get-premium-banner_oxh6r')
			? !!vaFeatures.isAiEnabledInAdminHub
			: true);

	const projectHasVirtualAgent: boolean =
		virtualAgentConfiguration?.__typename === 'VirtualAgentConfiguration';

	switch (allowMode) {
		case 'configured-and-entitled': {
			if (projectHasVirtualAgent) {
				if (siteCanUseVirtualAgent) {
					return <>{children}</>;
				}
				return (
					<Redirect
						to={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/settings`}
					/>
				);
			}
			return <Redirect to={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent`} />;
		}
		case 'any-configured': {
			if (projectHasVirtualAgent) {
				return <>{children}</>;
			}
			return <Redirect to={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent`} />;
		}
		case 'not-configured': {
			if (projectHasVirtualAgent) {
				return (
					<Redirect
						to={`/jira/servicedesk/projects/${projectKey}/settings/virtual-agent/settings`}
					/>
				);
			}
			return <>{children}</>;
		}
		default: {
			const _: never = allowMode;
		}
	}

	return null;
};
